import React, { useState, useEffect, useCallback } from "react";
import SaveButtonWithModal from "./SaveButtonWithModel";
import { uploadToCloudinary } from "../utils/CloudinaryUpload";

const Profile = ({
  formData,
  onProfileImageChange,
  onIdUrlChange,
  onSave
}) => {
  const [profileImage, setProfileImage] = useState(() => {
    const savedData = localStorage.getItem('profileImageData');
    return savedData ? JSON.parse(savedData) : null;
  });
  const [idFile, setIdFile] = useState(null);
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Add this function to handle saving to localStorage
  const handleSaveToLocalStorage = useCallback(() => {
    const dataToSave = {
      profileImage,
      idImageUrl,
      formData
    };
    localStorage.setItem('profileSectionData', JSON.stringify(dataToSave));
    setError("Profile data saved locally!");
    setTimeout(() => setError(""), 3000);
  }, [profileImage, idImageUrl, formData]);

  const handleDeleteProfileImage = useCallback(() => {
    if (profileImage?.previewUrl) {
      URL.revokeObjectURL(profileImage.previewUrl);
    }
    setProfileImage(null);
    localStorage.removeItem('profileImageData');
    setError("");
  }, [profileImage]);

  const handleImageChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      setError("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should be less than 5MB");
      return;
    }

    try {
      // Revoke previous URL if exists
      if (profileImage?.previewUrl) {
        URL.revokeObjectURL(profileImage.previewUrl);
      }

      const previewUrl = URL.createObjectURL(file);
      
      const imageData = {
        file,
        name: file.name,
        type: file.type,
        size: file.size,
        previewUrl,
        url: previewUrl, // Temporary URL until uploaded
        cloudinaryUrl: null
      };
      const formattedImage = {
        file: imageData.file,
        url: imageData.url,
        previewUrl: imageData.previewUrl,
        cloudinaryUrl: imageData.cloudinaryUrl,
        documentType: 'Profile Image'
      };
      setProfileImage(imageData);
      
      setError("");
      
      if (onProfileImageChange) {
        onProfileImageChange(imageData);
      }

    } catch (error) {
      console.error("Error handling image:", error);
      setError("Failed to process image. Please try again.");
    }
  }, [onProfileImageChange, profileImage]);

  const handleIdChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      // Revoke previous URL if exists
      if (idImageUrl) {
        URL.revokeObjectURL(idImageUrl);
      }
      
      const newUrl = URL.createObjectURL(file);
      setIdFile(file);
      setIdImageUrl(newUrl);
      setError("");
      
      if (onIdUrlChange) {
        onIdUrlChange(newUrl);
      }
    }
  }, [idImageUrl, onIdUrlChange]);

  const handleSave = useCallback(async () => {
    setError("");
    setSaving(true);
    
    try {
      let profileImageUrl = null;
      
      if (profileImage) {
        if (profileImage.file) {
          try {
            const uploadResult = await uploadToCloudinary(profileImage.file, {
              folder: 'profiles',
              userId: formData.idNumber
            });
            
            const updatedImage = {
              ...profileImage,
              cloudinaryUrl: uploadResult.url,
              url: uploadResult.url
            };
            
            setProfileImage(updatedImage);
            localStorage.setItem('profileImageData', JSON.stringify(updatedImage));
            profileImageUrl = uploadResult.url;
            
            if (onProfileImageChange) {
              onProfileImageChange(updatedImage);
            }
          } catch (uploadError) {
            console.warn("Profile image upload failed, using local URL:", uploadError);
            profileImageUrl = profileImage.previewUrl;
          }
        } else {
          profileImageUrl = profileImage.cloudinaryUrl || profileImage.url || profileImage.previewUrl || null;
        }
      }
    
      if (onSave) {
        await onSave({
          profileImage: profileImageUrl,
          idDocument: idImageUrl
        });
      }
  
    } catch (error) {
      setError(error.message || "Failed to save profile. Please try again.");
      console.error("Save error:", error);
    } finally {
      setSaving(false);
    }
  }, [profileImage, formData.idNumber, onProfileImageChange, onSave, idImageUrl]);

  useEffect(() => {
    return () => {
      // Cleanup object URLs
      if (profileImage?.previewUrl) URL.revokeObjectURL(profileImage.previewUrl);
      if (idImageUrl) URL.revokeObjectURL(idImageUrl);
    };
  }, [profileImage, idImageUrl]);

  useEffect(() => {
    const savedData = localStorage.getItem('profileImageData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setProfileImage(parsedData);
      } catch (e) {
        console.error("Failed to parse saved image data:", e);
        localStorage.removeItem('profileImageData');
      }
    }
  }, []);
// In your Profile component
const handleImageUpload = (file) => {
  const imageData = {
    file,
    previewUrl: URL.createObjectURL(file),
    url: null, // Will be set after upload
    cloudinaryUrl: null, // Will be set after upload
    documentType: 'Profile Image'
  };
  
  setProfileImage(imageData);
  
  // Upload to Cloudinary if needed
  uploadToCloudinary(file).then(uploadResult => {
    setProfileImage(prev => ({
      ...prev,
      url: uploadResult.secure_url,
      cloudinaryUrl: uploadResult.secure_url
    }));
  });
}
  return (
    <div className="md:px-0 md:mt-6">
      <div className="">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <div className="relative cursor-pointer">
              <div className="flex items-center gap-1">
                <span className="text-[8px] md:text-[36px] font-bold">
                  Profile
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <p className="font-normal italic text-[6px] md:text-[24px]">
              Please insert pictures / images for profile
            </p>
            {/* Add this button */}
         
          </div>
        </div>
        <div className="grid grid-cols-1 md:gap-2 gap-1 ">
          <div className="flex flex-col md:-mt-2">
            <div className="relative bg-[#D9D9D9] rounded-[5px] h-[140px] md:h-[650px] w-full overflow-hidden">
              {profileImage ? (
                <img
                  src={profileImage.url || profileImage.previewUrl}
                  alt="Profile"
                  className="object-cover w-full h-full"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyIDJDNi40NyAyIDIgNi40NyAyIDEyczQuNDcgMTAgMTAgMTAgMTAtNC40NyAxMC0xMFMxNy41MyAyIDEyIDJ6bTAgMThjLTQuNDEgMC04LTMuNTktOC04czMuNTktOCA4LTggOCAzLjU5IDggOC0zLjU5IDgtOCA4eiIvPjwvc3ZnPg==';
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-[#D9D9D9]">
                  <span className="text-gray-400 text-sm md:text-lg">No image selected</span>
                </div>
              )}
              <label className="absolute top-1 right-1 md:top-3 md:right-3 cursor-pointer">
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745668623/Add_square_duotone_gz7vxe.png"
                  alt="Add Icon"
                  className="w-4 md:w-[40px] h-4 md:h-[40px]"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
                <div 
                  className="absolute md:bottom-2 md:right-2 bottom-1 right-1"
                  onClick={handleDeleteProfileImage}
                >
                  <img
                    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1746296659/Trash_ya7kiu.png"
                    alt="Trash Icon"
                    className="w-3 md:w-[24px] h-3 md:h-[24px] cursor-pointer"
                  />
                </div>
            </div>
          </div>
        </div>
      </div>
      {error && (
        <div className="mt-2 text-red-600 text-xs md:text-base text-center">
          {error}
        </div>
      )}
      <SaveButtonWithModal onClick={handleSave} loading={saving} />
    </div>
  );
};

export default Profile;